import {YMaps, Map as YMap, FullscreenControl, Placemark, Clusterer} from "@pbe/react-yandex-maps";
import {useRef, useState, useCallback, useEffect, useMemo} from 'react';
import {APIv1} from '../../../../api';
import {message, Select} from 'antd';
import DMapListComponent from "../DMapListComponents/DMapListComponent";
import './DMapComponents.css';

export default function DMapComponents() {
    const [mapData, setMapData] = useState([]);
    const [userData, setUserData] = useState({});
    const [softwareData, setSoftwareData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [selectedSoftware, setSelectedSoftware] = useState(null);
    const [searchSerial, setSearchSerial] = useState("");
    const [activeSearch, setActiveSearch] = useState("");

    const mapRef = useRef(null);
    const clustererRef = useRef(null);
    const [clickedClusterItems, setClickedClusterItems] = useState([]);
    const [isListVisible, setIsListVisible] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [filterStatus, setFilterStatus] = useState("all");

    const [mapReady, setMapReady] = useState(false);
    const [clustererKey, setClustererKey] = useState(0);

    const fetchData = useCallback(
        async (
            company = selectedCompany,
            software = selectedSoftware,
        ) => {
            if (!userData.token) return;
            setLoading(true);
            try {
                const params = {};
                if (company) params.company_inn = company;
                if (software) params.software__name = software;

                const response = await APIv1.get('/device/maps/', {
                    params,
                    headers: {Authorization: `Token ${userData.token}`},
                });

                const results = Array.isArray(response.data)
                    ? response.data
                    : Array.isArray(response.data.results)
                        ? response.data.results
                        : [];

                const unique = Array.from(
                    new Map(results.map(item => [item.id, item])).values()
                );

                setMapData(unique);
            } catch (err) {
                console.error('Failed to load data:', err);
                message.error('Failed to load coordinates');
            } finally {
                setLoading(false);
            }
        },
        [userData.token, selectedCompany, selectedSoftware]
    );

    useEffect(() => {
        const fetchSoftwareList = async () => {
            try {
                const response = await APIv1.get('/software/list/');
                const result = response.data.results.map((sw) => ({
                    text: sw.name,
                    value: sw.name,
                }));
                setSoftwareData(result);
            } catch (error) {
                console.error('Failed to load software list:', error);
            }
        };
        fetchSoftwareList();
    }, []);

    useEffect(() => {
        const items = JSON.parse(localStorage.getItem('user'));
        if (items) setUserData(items);
    }, []);

    useEffect(() => {
        if (userData.token) {
            fetchData(selectedCompany, selectedSoftware);
        }
    }, [selectedCompany, selectedSoftware, userData.token]);

    // FIX: increment clustererKey via state so React actually re-renders
    useEffect(() => {
        setClickedClusterItems([]);
        setIsListVisible(false);
        setSelectedId(null);
        setClustererKey(k => k + 1);
    }, [filterStatus]);

    const filteredData = useMemo(() => mapData.filter(item => {
        const isDisconnected = item.is_disconnected === true;
        const isActive = item.is_active === true;

        if (filterStatus === "active" && !isActive) return false;
        if (filterStatus === "inactive" && (isActive || isDisconnected)) return false;
        if (filterStatus === "disconnected" && !isDisconnected) return false;

        if (activeSearch) {
            return item.device_serial_number
                ?.toLowerCase()
                .includes(activeSearch.toLowerCase());
        }

        return true;
    }), [mapData, filterStatus, activeSearch]);

    const handleClusterClick = (e) => {
        const target = e.get('target');
        if (!target.getGeoObjects) return;

        const clusterGeoObjects = target.getGeoObjects();
        const clusterCoords = clusterGeoObjects.map(obj => obj.geometry.getCoordinates());

        const itemsInCluster = filteredData.filter(item =>
            clusterCoords.some(coords =>
                Number(item.lat).toFixed(6) === Number(coords[0]).toFixed(6) &&
                Number(item.long).toFixed(6) === Number(coords[1]).toFixed(6)
            )
        );

        setClickedClusterItems(itemsInCluster);
        setIsListVisible(true);
    };

    const handlePlacemarkClick = (item) => {
        setClickedClusterItems([item]);
        setIsListVisible(true);
        setSelectedId(item.id);
    };

    const handleListItemClick = (item) => {
        setSelectedId(item.id);
        if (mapRef.current) {
            mapRef.current.setCenter([parseFloat(item.lat), parseFloat(item.long)], 16, {
                duration: 300,
            });
        }
    };

    const handleSearchKeyDown = (e) => {
        if (e.key === 'Enter') {
            setActiveSearch(e.target.value.trim());
        }
        if (e.key === 'Escape') {
            setSearchSerial("");
            setActiveSearch("");
        }
    };

    useEffect(() => {
        if (filteredData.length === 1 && filteredData[0].lat && filteredData[0].long) {
            mapRef.current?.setCenter(
                [parseFloat(filteredData[0].lat), parseFloat(filteredData[0].long)],
                16,
                {duration: 300}
            );
        }
    }, [filteredData]);

    const companyOptions = Array.from(
        new Map(
            mapData.map(item => [
                String(item.company__inn),
                {label: item.company__name, value: item.company__inn},
            ])
        ).values()
    );

    const softwareOptions = Array.from(
        new Map(
            softwareData.map(item => [item.value, {label: item.text, value: item.value}])
        ).values()
    );

    return (
        <div className="map-overlay-container">
            <div className="serial-search-bar">
                <input
                    type="text"
                    placeholder="Search by serial number..."
                    value={searchSerial}
                    onChange={e => setSearchSerial(e.target.value)}
                    onKeyDown={handleSearchKeyDown}
                    autoFocus
                    className="serial-search-input"
                />
                {activeSearch && (
                    <button
                        className="serial-search-clear"
                        onClick={() => {
                            setSearchSerial("");
                            setActiveSearch("");
                        }}
                    >
                        ✕
                    </button>
                )}
            </div>

            <div className="filter-buttons">
                <button
                    className={filterStatus === "all" ? "active-all" : ""}
                    onClick={() => setFilterStatus("all")}
                >All
                </button>
                <button
                    className={filterStatus === "active" ? "active-active" : ""}
                    onClick={() => setFilterStatus("active")}
                >Active
                </button>
                <button
                    className={filterStatus === "inactive" ? "active-inactive" : ""}
                    onClick={() => setFilterStatus("inactive")}
                >Not Active
                </button>
                <button
                    className={filterStatus === "disconnected" ? "active-disconnected" : ""}
                    onClick={() => setFilterStatus("disconnected")}
                >Disconnected
                </button>

                <Select
                    placeholder="Filter by company"
                    value={selectedCompany ?? undefined}
                    onChange={setSelectedCompany}
                    options={companyOptions}
                    allowClear
                    virtual={false}
                    dropdownMatchSelectWidth={false}
                    showSearch
                    filterOption={(input, option) =>
                        option?.label?.toLowerCase().includes(input.toLowerCase())
                    }
                    loading={loading}
                />
                <Select
                    placeholder="Filter by software"
                    value={selectedSoftware ?? undefined}
                    onChange={setSelectedSoftware}
                    options={softwareOptions}
                    allowClear
                    virtual={false}
                    dropdownMatchSelectWidth={false}
                    showSearch
                    filterOption={(input, option) =>
                        option?.label?.toLowerCase().includes(input.toLowerCase())
                    }
                    loading={loading}
                />
            </div>

            <YMaps>
                <YMap
                    instanceRef={mapRef}
                    defaultState={{center: [41.31, 69.28], zoom: 12}}
                    className="map-full"
                    onLoad={() => setMapReady(true)}
                >
                    <FullscreenControl/>

                    {mapReady && (
                        <Clusterer
                            key={clustererKey}
                            instanceRef={clustererRef}
                            options={{
                                preset: 'islands#invertedBlueClusterIcons',
                                gridSize: 60,
                                minClusterSize: 3,
                                disableClickZoom: true,
                            }}
                            onClick={handleClusterClick}
                        >
                            {filteredData.map(item =>
                                item.lat && item.long ? (
                                    <Placemark
                                        key={`placemark-${item.id}`}
                                        geometry={[parseFloat(item.lat), parseFloat(item.long)]}
                                        onClick={() => handlePlacemarkClick(item)}
                                        properties={{
                                            balloonContentHeader: item.company__name,
                                            balloonContentBody: `${item.device_serial_number} (${item.market__name})`,
                                        }}
                                        options={{
                                            preset:
                                                item.is_disconnected
                                                    ? 'islands#grayIcon'
                                                    : selectedId === item.id
                                                        ? 'islands#blueIcon'
                                                        : item.is_active
                                                            ? 'islands#greenIcon'
                                                            : 'islands#redIcon',
                                        }}
                                    />
                                ) : null
                            )}
                        </Clusterer>
                    )}
                </YMap>
            </YMaps>

            {isListVisible && (
                <div className="list-overlay">
                    <h3 className="list-title">
                        📍 Selected Items ({clickedClusterItems.length})
                    </h3>
                    <div className="list-content">
                        <DMapListComponent
                            mapData={clickedClusterItems}
                            loading={loading}
                            onItemClick={handleListItemClick}
                        />
                    </div>
                    <button className="close-btn" onClick={() => setIsListVisible(false)}>
                        Close
                    </button>
                </div>
            )}
        </div>
    );
}
